import React, { forwardRef } from "react";
import type { ForwardedRef } from "react";
import {
  Label as HeadlessLabel,
  ProgressBar as HeadlessProgressBar,
} from "react-aria-components";

import { Text } from "../../Text";
import styles from "./styles.module.css";
import type { ProgressProps } from "./types";

/**
 * Circular ring geometry. A 48×48 viewBox with a 6px stroke leaves a
 * centerline radius of 21px; the determinate arc is drawn by offsetting the
 * ring's stroke-dash by the un-completed share of the circumference. The
 * values are SVG attributes (not CSSOM styles) so they survive jsdom and are
 * assertable in tests.
 */
const RING_VIEWBOX = 48;
const RING_CENTER = RING_VIEWBOX / 2;
const RING_STROKE_WIDTH = 6;
const RING_RADIUS = RING_CENTER - RING_STROKE_WIDTH / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const _Progress = (props: ProgressProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    color = "accent",
    isIndeterminate = false,
    label,
    showValueLabel = false,
    variant = "linear",
    ...rest
  } = props;

  const hasVisibleLabel = typeof label === "string" && label.trim().length > 0;

  return (
    <HeadlessProgressBar
      {...rest}
      className={styles.progress}
      data-color={color}
      data-variant={variant}
      isIndeterminate={isIndeterminate}
      ref={ref}
    >
      {({ isIndeterminate: indeterminate, percentage = 0, valueText }) => {
        const showValue = Boolean(showValueLabel) && !indeterminate;

        return (
          <>
            {hasVisibleLabel && (
              <HeadlessLabel className={styles.label}>
                <Text lineClamp={1} size="caption">
                  {label}
                </Text>
              </HeadlessLabel>
            )}

            {variant === "circular" ? (
              <span className={styles.ringWrapper} data-ring-wrapper="">
                <svg
                  aria-hidden="true"
                  className={styles.ring}
                  viewBox={`0 0 ${RING_VIEWBOX} ${RING_VIEWBOX}`}
                >
                  <circle
                    className={styles.ringTrack}
                    cx={RING_CENTER}
                    cy={RING_CENTER}
                    data-ring-track=""
                    r={RING_RADIUS}
                    strokeWidth={RING_STROKE_WIDTH}
                  />
                  <circle
                    className={styles.ringFill}
                    cx={RING_CENTER}
                    cy={RING_CENTER}
                    data-ring-fill=""
                    r={RING_RADIUS}
                    strokeDasharray={
                      indeterminate
                        ? undefined
                        : `${RING_CIRCUMFERENCE} ${RING_CIRCUMFERENCE}`
                    }
                    strokeDashoffset={
                      indeterminate
                        ? undefined
                        : RING_CIRCUMFERENCE * (1 - percentage / 100)
                    }
                    strokeWidth={RING_STROKE_WIDTH}
                  />
                </svg>
                {showValue && (
                  <Text
                    className={styles.valueLabel}
                    data-value-label=""
                    size="footnote"
                  >
                    {valueText}
                  </Text>
                )}
              </span>
            ) : (
              <>
                {showValue && (
                  <Text
                    className={styles.valueLabel}
                    data-value-label=""
                    size="caption"
                  >
                    {valueText}
                  </Text>
                )}
                <span aria-hidden="true" className={styles.track} data-track="">
                  <span
                    className={styles.fill}
                    data-fill=""
                    // The width travels as an inline CUSTOM property (consumed
                    // by styles.module.css as inline-size): jsdom's CSSOM
                    // drops var()/percentage longhands, custom props survive
                    // and stay assertable. Same contract as the Divider.
                    style={
                      indeterminate
                        ? undefined
                        : ({
                            "--progress-value": `${percentage}%`,
                          } as React.CSSProperties)
                    }
                  />
                </span>
              </>
            )}
          </>
        );
      }}
    </HeadlessProgressBar>
  );
};

/**
 * Progress shows determinate or indeterminate progress of a running task,
 * as a linear bar or a circular ring. Token-driven; the indeterminate
 * circular state reuses the Spinner's visual language (1s linear rotation).
 */
export const Progress = forwardRef(_Progress);
